export const translations = {
  en: {
    _dir: "ltr",
    _name: "English",
    app: { title: "Attendance System" },
    login: {
      title: "Welcome back",
      subtitle: "Sign in to manage attendance",
      phone: "Phone number or email",
      phonePh: "07500000000 / name@email.com",
      password: "Password",
      passwordPh: "Enter your password",
      submit: "Sign in",
      signing: "Signing in...",
      or: "OR",
      google: "Sign in with Google",
    },
    forgot: {
      link: "Forgot password?",
      title: "Reset password",
      subtitle: "Choose how to receive your verification code",
      channel: "Send code via",
      email: "Email",
      sms: "SMS (text message)",
      emailAddr: "Email address",
      phone: "Phone number",
      sendCode: "Send code",
      codeSent: "If an account exists, a code has been sent",
      codeHint: "Enter the code you received and your new password",
      code: "Verification code",
      newPassword: "New password",
      confirm: "Confirm new password",
      reset: "Reset password",
      back: "Back",
      mismatch: "Passwords do not match",
      tooShort: "Password must be at least 6 characters",
      success: "Password reset successfully. You can sign in now.",
    },
    nav: {
      attendance: "Attendance",
      monthly: "Monthly",
      absentees: "Absentees",
      groups: "Groups",
      admins: "Admins",
      audit: "Audit Log",
      settings: "Settings",
      newYear: "New School Year",
      logout: "Log out",
      loggedInAs: "Signed in as",
    },
    audit: {
      title: "Audit Log",
      subtitle: "A trail of logins, security events and data changes across the school",
      action: "Event type",
      allActions: "All events",
      search: "Search",
      searchPh: "Search by name, phone or detail",
      total: "Total",
      time: "Time",
      event: "Event",
      actor: "Performed by",
      target: "Target",
      detail: "Details",
      empty: "No matching activity yet.",
      loadMore: "Load more",
      actions: {
        login_success: "Login", login_failed: "Failed login", login_lockout: "Account locked",
        password_change: "Password changed", password_reset: "Password reset",
        student_create: "Student added", student_update: "Student edited", student_delete: "Student removed",
        group_create: "Group added", group_update: "Group edited", group_delete: "Group removed",
        admin_create: "Admin added", admin_update: "Admin edited", admin_delete: "Admin removed",
      },
    },
    roles: { super_admin: "A (Full access)", assistant: "Admin Assistant", principal: "Principal", admin: "Admin (one group)", pending: "Waiting for Consent" },
    register: {
      link: "Create a new account",
      title: "Create account",
      name: "Full name",
      email: "Email",
      phone: "Phone number (optional)",
      password: "Password (at least 6 characters)",
      note: "Your account will wait for the Super Admin's approval before you can see any data.",
      submit: "Create account",
      success: "Your account was created. It is waiting for the Super Admin's approval. You can sign in with your email and password after you are approved.",
      ok: "OK",
    },
    pending: {
      title: "Waiting for approval",
      text: "Your account was created but has no role yet. Please wait until the school's Super Admin gives you access.",
      logout: "Log out",
    },
    newYear: {
      title: "Start a new school year",
      subtitle: "Last year's attendance is always archived first, so nothing is lost.",
      label: "Name of the year to archive",
      promote: "Move students up one group (optional)",
      stay: "Stay in this group",
      clear: "Clear attendance after archiving",
      clearHelp: "The new year starts with an empty attendance sheet. Last year's records stay in the archive below.",
      start: "Start new year",
      confirm: "Start the new school year now?",
      done: "New year started (archived / moved / cleared)",
      archives: "Archived years",
      noArchives: "No archived years yet.",
      records: "attendance records",
    },
    profile: {
      changePassword: "Change password",
      subtitle: "Update your login password",
      current: "Current password",
      currentHint: "Leave blank if your account has no password yet (e.g. Google sign-in).",
      new: "New password",
      confirm: "Confirm new password",
      save: "Update password",
      mismatch: "New passwords do not match",
      tooShort: "Password must be at least 6 characters",
      success: "Password updated successfully",
    },
    attendance: {
      title: "Daily Attendance",
      subtitle: "Mark students present, absent or on leave",
      date: "Date",
      group: "Group / Class",
      allGroups: "All groups",
      number: "ID",
      student: "Student name",
      status: "Status",
      present: "Present",
      absent: "Absent",
      leave: "Leave",
      markLeave: "Mark leave",
      notMarked: "Not marked",
      actions: "Actions",
      markPresent: "Mark present",
      markAbsent: "Mark absent",
      saved: "Attendance updated",
      clear: "Clear",
      cleared: "Mark removed",
      viewHistory: "View history",
      exportGroup: "Export to Excel",
      exportAll: "Export all groups",
      exporting: "Exporting...",
      presentCount: "Present",
      absentCount: "Absent",
      leaveCount: "Leave",
      totalStudents: "Students",
      noStudents: "No students in this group.",
      search: "Search all students by name, ID or phone...",
      searchResults: "Search results",
      dateRange: "Report date range",
      startDate: "Start date",
      endDate: "End date",
      clearRange: "Clear range",
      rangeActive: "Range applied to reports & export",
      selectAll: "Select all",
      deleteSelected: "Delete selected",
      selectedCount: "selected",
      confirmDeleteSelected: "Delete the selected students and all their attendance records?",
      studentsDeleted: "Students deleted",
      periodHint: "Attendance can be recorded within this period",
      deleteAllInGroup: "Delete all in group",
      confirmDeleteAllInGroup: "Delete ALL students in \"{group}\" and their attendance records? This cannot be undone.",
      groupCleared: "All students in the group deleted",
      printGroup: "Print group list",
      rosterTitle: "Group Student List",
      totalPresent: "Present",
      totalAbsent: "Absent",
      addStudent: "Add student",
      studentAdded: "Student added",
      deleteStudent: "Delete student",
      studentDeleted: "Student deleted",
      confirmDeleteStudent: "Delete this student and all their attendance records?",
    },
    student: {
      newTitle: "Add student",
      editTitle: "Edit student",
      name: "Full name",
      group: "Group / Class",
      gender: "Gender",
      male: "Male",
      female: "Female",
      phone1: "Phone number 1",
      phone2: "Phone number 2",
      save: "Save",
      cancel: "Cancel",
    },
    detail: {
      back: "Back",
      title: "Attendance History",
      group: "Group / Class",
      id: "Student ID",
      present: "Days Present",
      absent: "Days Absent",
      leave: "Days on Leave",
      percentage: "Attendance",
      total: "Total Days",
      history: "Full history by date",
      date: "Date",
      status: "Status",
      print: "Print report",
      delete: "Delete",
      deleted: "Record deleted",
      confirmDelete: "Delete this attendance record?",
      noRecords: "No attendance records yet.",
      presentDates: "Present dates",
      absentDates: "Absent dates",
      leaveDates: "Leave dates",
      reportTitle: "Student Attendance Report",
      generated: "Generated on",
      dateRange: "Date range",
      gender: "Gender",
      male: "Male",
      female: "Female",
      phone1: "Phone 1",
      phone2: "Phone 2",
      notes: "Notes & observations",
      notesPlaceholder: "Add comments or observations about this student...",
      saveNotes: "Save notes",
      notesSaved: "Notes saved",
      editStudent: "Edit",
      deleteStudent: "Delete student",
      studentDeleted: "Student deleted",
      confirmDeleteStudent: "Delete this student and all their attendance records?",
    },
    admins: {
      title: "Admin Accounts",
      subtitle: "Manage accounts and their roles",
      add: "Add admin",
      name: "Full name",
      phone: "Phone number",
      email: "Email (for Google sign-in)",
      emailPh: "name@example.com",
      password: "Password",
      passwordEdit: "New password (leave blank to keep)",
      role: "Role",
      group: "Visible group",
      allGroups: "All groups",
      actions: "Actions",
      edit: "Edit",
      delete: "Delete",
      save: "Save",
      cancel: "Cancel",
      created: "Admin created",
      updated: "Admin updated",
      approve: "Approve",
      approved: "Account approved",
      reject: "Reject",
      rejected: "Request rejected and removed",
      deleted: "Admin removed",
      confirmDelete: "Remove this admin account?",
      you: "You",
      newTitle: "New admin",
      editTitle: "Edit admin",
    },
    groups: {
      title: "Groups / Classes",
      subtitle: "Add and rename your groups",
      add: "Add group",
      name: "Group name",
      namePh: "e.g. Grade 4 - A",
      students: "Students",
      actions: "Actions",
      rename: "Rename",
      delete: "Delete",
      save: "Save",
      cancel: "Cancel",
      created: "Group created",
      renamed: "Group renamed",
      deleted: "Group deleted",
      confirmDelete: "Delete this group? (only works if it has no students)",
      newTitle: "New group",
      editTitle: "Rename group",
      empty: "No groups yet. Add your first group.",
    },
    importStudents: {
      button: "Upload students file",
      title: "Upload students from a file",
      help: "Excel (.xlsx) or CSV file with columns: Name, Phone, Gender. The first row can be a header.",
      group: "Add students to group",
      choose: "Choose file",
      upload: "Upload",
      uploading: "Uploading...",
      done: "{created} students added, {skipped} skipped",
      template: "Download sample file",
      cancel: "Cancel",
    },
    backup: {
      title: "Move database to a new server",
      help: "Download a full backup of all school data (settings, groups, admins, students, attendance, logs). On the new server, sign in as Super Admin and restore that file here.",
      download: "Download full backup",
      restore: "Restore from backup file",
      confirm: "Restore this backup? Matching records will be replaced with the backup's version.",
      restored: "Backup restored successfully",
      working: "Working...",
      downloadFailed: "Download failed",
    },
    settings: {
      title: "School & Appearance",
      subtitle: "Customize your school branding",
      schoolName: "School name",
      logo: "School logo",
      uploadLogo: "Upload logo",
      uploading: "Uploading...",
      primaryColor: "Primary color",
      backgroundColor: "Background color",
      pageStyle: "Page style",
      styleModern: "Modern",
      styleClassic: "Classic",
      styleCompact: "Compact",
      save: "Save changes",
      saved: "Settings saved",
      preview: "Live preview",
      welcomeSection: "Welcome / Login screen",
      welcomeImage: "Welcome image",
      uploadWelcome: "Upload image",
      welcomeTitle: "Welcome title",
      welcomeTitlePh: "Leave blank to use the default",
      welcomeSubtitle: "Welcome subtitle",
      imagePosition: "Image position",
      posLeft: "Left",
      posRight: "Right",
      posNone: "Hidden (form only)",
      textPosition: "Text position",
      posTop: "Top",
      posCenter: "Center",
      posBottom: "Bottom",
      textAlign: "Text alignment",
      alignStart: "Start",
      alignCenter: "Center",
      alignEnd: "End",
    },
    common: { loading: "Loading...", language: "Language", cancel: "Cancel", confirm: "Confirm" },
    monthly: {
      title: "Monthly Attendance",
      subtitle: "Track attendance across the whole month",
      month: "Month",
      group: "Group / Class",
      allGroups: "All groups",
      student: "Student",
      present: "Present",
      absent: "Absent",
      percentage: "%",
      leave: "Leave",
      legend: "Click a day to cycle: Present → Absent → Leave → clear",
      noData: "No students to show.",
      saved: "Attendance updated",
      print: "Print month",
      reportTitle: "Monthly Attendance Records",
    },
    absentees: {
      title: "Absentees Overview",
      subtitle: "See all students who are absent or on leave",
      byDate: "By date",
      ranking: "Totals / ranking",
      date: "Date",
      group: "Group / Class",
      allGroups: "All groups",
      from: "From",
      to: "To",
      absent: "Absent",
      leave: "On leave",
      student: "Student",
      number: "ID",
      phone: "Phone",
      totalAbsent: "Total Absent",
      totalLeave: "Total Leave",
      percentage: "Attendance %",
      noAbsent: "No absences on this date.",
      noLeave: "No one on leave on this date.",
      noData: "No absences in this period.",
      refresh: "Refresh",
      autoRefresh: "Auto-refresh",
      updated: "Updated",
      print: "Print",
    },
  },
  ku: {
    _dir: "rtl",
    _name: "کوردی",
    app: { title: "سیستەمی ئامادەبوون" },
    login: {
      title: "بەخێربێیتەوە",
      subtitle: "بچۆ ژوورەوە بۆ بەڕێوەبردنی ئامادەبوون",
      phone: "ژمارەی مۆبایل یان ئیمەیڵ",
      phonePh: "07500000000 / name@email.com",
      password: "وشەی نهێنی",
      passwordPh: "وشەی نهێنیت بنووسە",
      submit: "چوونەژوورەوە",
      signing: "چوونەژوورەوە...",
      or: "یان",
      google: "چوونەژوورەوە بە گووگڵ",
    },
    forgot: {
      link: "وشەی نهێنیت لەبیرچووە؟",
      title: "ڕێکخستنەوەی وشەی نهێنی",
      subtitle: "هەڵبژێرە چۆن کۆدی پشتڕاستکردنەوە وەربگریت",
      channel: "ناردنی کۆد لەڕێگەی",
      email: "ئیمەیڵ",
      sms: "پەیامی کورت (SMS)",
      emailAddr: "ناونیشانی ئیمەیڵ",
      phone: "ژمارەی مۆبایل",
      sendCode: "ناردنی کۆد",
      codeSent: "ئەگەر هەژمارێک هەبێت، کۆدێک نێردرا",
      codeHint: "کۆدی وەرگیراو و وشەی نهێنی نوێت بنووسە",
      code: "کۆدی پشتڕاستکردنەوە",
      newPassword: "وشەی نهێنی نوێ",
      confirm: "دووبارەکردنەوەی وشەی نهێنی نوێ",
      reset: "ڕێکخستنەوەی وشەی نهێنی",
      back: "گەڕانەوە",
      mismatch: "وشە نهێنییەکان وەک یەک نین",
      tooShort: "وشەی نهێنی دەبێت لانیکەم ٦ پیت بێت",
      success: "وشەی نهێنی بە سەرکەوتوویی ڕێکخرایەوە. ئێستا دەتوانیت بچیتە ژوورەوە.",
    },
    nav: {
      attendance: "ئامادەبوون",
      monthly: "مانگانە",
      absentees: "ئامادەنەبووان",
      groups: "پۆلەکان",
      admins: "بەڕێوەبەران",
      audit: "تۆماری چالاکی",
      settings: "ڕێکخستنەکان",
      newYear: "ساڵی خوێندنی نوێ",
      logout: "چوونەدەرەوە",
      loggedInAs: "چوویتەژوورەوە وەک",
    },
    audit: {
      title: "تۆماری چالاکی",
      subtitle: "تۆمارێک لە چوونەژوورەوەکان، ڕووداوە ئەمنییەکان و گۆڕانکارییەکانی داتا",
      action: "جۆری ڕووداو",
      allActions: "هەموو ڕووداوەکان",
      search: "گەڕان",
      searchPh: "گەڕان بەپێی ناو، مۆبایل یان وردەکاری",
      total: "کۆی گشتی",
      time: "کات",
      event: "ڕووداو",
      actor: "ئەنجامدراو لەلایەن",
      target: "ئامانج",
      detail: "وردەکاری",
      empty: "هیچ چالاکییەک نەدۆزرایەوە.",
      loadMore: "زیاتر",
      actions: {
        login_success: "چوونەژوورەوە", login_failed: "چوونەژوورەوەی سەرنەکەوتوو", login_lockout: "هەژمار قوفڵکرا",
        password_change: "وشەی نهێنی گۆڕدرا", password_reset: "وشەی نهێنی ڕێکخرایەوە",
        student_create: "قوتابی زیادکرا", student_update: "قوتابی دەستکاریکرا", student_delete: "قوتابی سڕایەوە",
        group_create: "پۆل زیادکرا", group_update: "پۆل دەستکاریکرا", group_delete: "پۆل سڕایەوە",
        admin_create: "بەڕێوەبەر زیادکرا", admin_update: "بەڕێوەبەر دەستکاریکرا", admin_delete: "بەڕێوەبەر سڕایەوە",
      },
    },
    roles: { super_admin: "A (دەسەڵاتی تەواو)", assistant: "یاریدەدەری بەڕێوەبەر", principal: "بەڕێوەبەری قوتابخانە", admin: "بەڕێوەبەر (یەک پۆل)", pending: "چاوەڕوانی ڕەزامەندی" },
    register: {
      link: "دروستکردنی هەژماری نوێ",
      title: "دروستکردنی هەژمار",
      name: "ناوی تەواو",
      email: "ئیمەیڵ",
      phone: "ژمارەی مۆبایل (ئارەزوومەندانە)",
      password: "وشەی نهێنی (لانیکەم ٦ پیت)",
      note: "هەژمارەکەت چاوەڕێی ڕەزامەندی سوپەر ئەدمین دەبێت پێش ئەوەی هیچ داتایەک ببینیت.",
      submit: "دروستکردنی هەژمار",
      success: "هەژمارەکەت دروستکرا و چاوەڕێی ڕەزامەندی سوپەر ئەدمینە. دوای ڕەزامەندی دەتوانیت بە ئیمەیڵ و وشەی نهێنییەکەت بچیتە ژوورەوە.",
      ok: "باشە",
    },
    pending: {
      title: "چاوەڕوانی ڕەزامەندی",
      text: "هەژمارەکەت دروستکرا بەڵام هێشتا ڕۆڵی نییە. تکایە چاوەڕێ بکە تا سوپەر ئەدمین دەسەڵاتت پێدەدات.",
      logout: "چوونەدەرەوە",
    },
    newYear: {
      title: "دەستپێکردنی ساڵی خوێندنی نوێ",
      subtitle: "ئامادەبوونی ساڵی ڕابردوو هەمیشە سەرەتا ئەرشیف دەکرێت، هیچ شتێک ون نابێت.",
      label: "ناوی ئەو ساڵەی ئەرشیف دەکرێت",
      promote: "گواستنەوەی قوتابیان بۆ پۆلی سەرەوە (ئارەزوومەندانە)",
      stay: "لەم پۆلەدا بمێنێتەوە",
      clear: "سڕینەوەی ئامادەبوون دوای ئەرشیفکردن",
      clearHelp: "ساڵی نوێ بە خشتەیەکی بەتاڵ دەست پێدەکات. تۆمارەکانی ساڵی ڕابردوو لە ئەرشیفی خوارەوە دەمێننەوە.",
      start: "دەستپێکردنی ساڵی نوێ",
      confirm: "ئێستا ساڵی خوێندنی نوێ دەست پێبکەیت؟",
      done: "ساڵی نوێ دەستی پێکرد (ئەرشیف / گواستنەوە / سڕینەوە)",
      archives: "ساڵە ئەرشیفکراوەکان",
      noArchives: "هێشتا هیچ ساڵێک ئەرشیف نەکراوە.",
      records: "تۆماری ئامادەبوون",
    },
    profile: {
      changePassword: "گۆڕینی وشەی نهێنی",
      subtitle: "وشەی نهێنی چوونەژوورەوەت نوێ بکەرەوە",
      current: "وشەی نهێنی ئێستا",
      currentHint: "بەتاڵی بهێڵەوە ئەگەر هەژمارەکەت وشەی نهێنی نییە (وەک چوونەژوورەوە بە گووگڵ).",
      new: "وشەی نهێنی نوێ",
      confirm: "دووبارەکردنەوەی وشەی نهێنی نوێ",
      save: "نوێکردنەوەی وشەی نهێنی",
      mismatch: "وشە نهێنییە نوێیەکان وەک یەک نین",
      tooShort: "وشەی نهێنی دەبێت لانیکەم ٦ پیت بێت",
      success: "وشەی نهێنی بە سەرکەوتوویی نوێکرایەوە",
    },
    attendance: {
      title: "ئامادەبوونی ڕۆژانە",
      subtitle: "قوتابیان بە ئامادە، ئامادەنەبوو یان مۆڵەت نیشان بکە",
      date: "بەروار",
      group: "پۆل / کۆمەڵ",
      allGroups: "هەموو پۆلەکان",
      number: "ژمارە",
      student: "ناوی قوتابی",
      status: "دۆخ",
      present: "ئامادە",
      absent: "ئامادەنەبوو",
      leave: "مۆڵەت",
      markLeave: "مۆڵەت",
      notMarked: "نیشان نەکراوە",
      actions: "کردارەکان",
      markPresent: "ئامادە",
      markAbsent: "ئامادەنەبوو",
      saved: "ئامادەبوون نوێکرایەوە",
      clear: "سڕینەوە",
      cleared: "نیشانەکە لابرا",
      viewHistory: "بینینی مێژوو",
      exportGroup: "هەناردن بۆ ئێکسڵ",
      exportAll: "هەناردنی هەموو پۆلەکان",
      exporting: "هەناردن...",
      presentCount: "ئامادە",
      absentCount: "ئامادەنەبوو",
      leaveCount: "مۆڵەت",
      totalStudents: "قوتابیان",
      noStudents: "هیچ قوتابیەک لەم پۆلەدا نییە.",
      search: "گەڕان بەناو، ژمارە یان مۆبایل...",
      searchResults: "ئەنجامی گەڕان",
      dateRange: "ماوەی بەرواری ڕاپۆرت",
      startDate: "بەرواری دەستپێک",
      endDate: "بەرواری کۆتایی",
      clearRange: "سڕینەوەی ماوە",
      rangeActive: "ماوە بۆ ڕاپۆرت و هەناردن جێبەجێکرا",
      selectAll: "هەڵبژاردنی هەموو",
      deleteSelected: "سڕینەوەی هەڵبژێردراوەکان",
      selectedCount: "هەڵبژێردراو",
      confirmDeleteSelected: "قوتابیە هەڵبژێردراوەکان و هەموو تۆمارەکانیان بسڕێتەوە؟",
      studentsDeleted: "قوتابیان سڕانەوە",
      periodHint: "ئامادەبوون تەنها لەم ماوەیەدا تۆمار دەکرێت",
      deleteAllInGroup: "سڕینەوەی هەموو قوتابیانی پۆل",
      confirmDeleteAllInGroup: "هەموو قوتابیانی «{group}» و تۆمارەکانی ئامادەبوونیان بسڕێتەوە؟ ناگەڕێتەوە.",
      groupCleared: "هەموو قوتابیانی پۆلەکە سڕانەوە",
      printGroup: "چاپکردنی لیستی پۆل",
      rosterTitle: "لیستی قوتابیانی پۆل",
      totalPresent: "ئامادە",
      totalAbsent: "ئامادەنەبوو",
      addStudent: "زیادکردنی قوتابی",
      studentAdded: "قوتابی زیادکرا",
      deleteStudent: "سڕینەوەی قوتابی",
      studentDeleted: "قوتابی سڕایەوە",
      confirmDeleteStudent: "ئەم قوتابیە و هەموو تۆمارەکانی ئامادەبوونی بسڕێتەوە؟",
    },
    student: {
      newTitle: "زیادکردنی قوتابی",
      editTitle: "دەستکاری قوتابی",
      name: "ناوی تەواو",
      group: "پۆل / کۆمەڵ",
      gender: "ڕەگەز",
      male: "نێر",
      female: "مێ",
      phone1: "ژمارەی مۆبایل ١",
      phone2: "ژمارەی مۆبایل ٢",
      save: "پاشەکەوت",
      cancel: "پاشگەزبوونەوە",
    },
    detail: {
      back: "گەڕانەوە",
      title: "مێژووی ئامادەبوون",
      group: "پۆل / کۆمەڵ",
      id: "ژمارەی قوتابی",
      present: "ڕۆژانی ئامادە",
      absent: "ڕۆژانی ئامادەنەبوو",
      leave: "ڕۆژانی مۆڵەت",
      percentage: "ڕێژەی ئامادەبوون",
      total: "کۆی ڕۆژان",
      history: "مێژووی تەواو بەپێی بەروار",
      date: "بەروار",
      status: "دۆخ",
      print: "چاپکردنی ڕاپۆرت",
      delete: "سڕینەوە",
      deleted: "تۆمارەکە سڕایەوە",
      confirmDelete: "ئەم تۆماری ئامادەبوونە بسڕێتەوە؟",
      noRecords: "هێشتا هیچ تۆمارێکی ئامادەبوون نییە.",
      presentDates: "بەرواری ئامادەبوون",
      absentDates: "بەرواری ئامادەنەبوون",
      leaveDates: "بەرواری مۆڵەت",
      reportTitle: "ڕاپۆرتی ئامادەبوونی قوتابی",
      generated: "دروستکراوە لە",
      dateRange: "ماوەی بەروار",
      gender: "ڕەگەز",
      male: "نێر",
      female: "مێ",
      phone1: "مۆبایل ١",
      phone2: "مۆبایل ٢",
      notes: "تێبینی و سەرنجەکان",
      notesPlaceholder: "تێبینی یان سەرنج دەربارەی قوتابی زیاد بکە...",
      saveNotes: "پاشەکەوتی تێبینی",
      notesSaved: "تێبینی پاشەکەوتکرا",
      editStudent: "دەستکاری",
      deleteStudent: "سڕینەوەی قوتابی",
      studentDeleted: "قوتابی سڕایەوە",
      confirmDeleteStudent: "ئەم قوتابیە و هەموو تۆمارەکانی بسڕێتەوە؟",
    },
    admins: {
      title: "هەژماری بەڕێوەبەران",
      subtitle: "بەڕێوەبردنی هەژمارەکان و ڕۆڵەکانیان",
      add: "زیادکردنی بەڕێوەبەر",
      name: "ناوی تەواو",
      phone: "ژمارەی مۆبایل",
      password: "وشەی نهێنی",
      passwordEdit: "وشەی نهێنی نوێ (بەتاڵی بهێڵەوە بۆ پاراستن)",
      role: "ڕۆڵ",
      group: "پۆلی دیار",
      allGroups: "هەموو پۆلەکان",
      actions: "کردارەکان",
      edit: "دەستکاری",
      delete: "سڕینەوە",
      save: "پاشەکەوت",
      cancel: "پاشگەزبوونەوە",
      created: "بەڕێوەبەر دروستکرا",
      updated: "بەڕێوەبەر نوێکرایەوە",
      approve: "ڕەزامەندی",
      approved: "هەژمارەکە ڕەزامەندی پێدرا",
      reject: "ڕەتکردنەوە",
      rejected: "داواکارییەکە ڕەتکرایەوە و سڕایەوە",
      deleted: "بەڕێوەبەر سڕایەوە",
      confirmDelete: "ئەم هەژمارە بەڕێوەبەرە بسڕێتەوە؟",
      you: "تۆ",
      newTitle: "بەڕێوەبەری نوێ",
      editTitle: "دەستکاری بەڕێوەبەر",
    },
    groups: {
      title: "پۆلەکان / کۆمەڵەکان",
      subtitle: "زیادکردن و گۆڕینی ناوی پۆلەکان",
      add: "زیادکردنی پۆل",
      name: "ناوی پۆل",
      namePh: "بۆ نموونە پۆلی ٤ - A",
      students: "قوتابیان",
      actions: "کردارەکان",
      rename: "گۆڕینی ناو",
      delete: "سڕینەوە",
      save: "پاشەکەوت",
      cancel: "پاشگەزبوونەوە",
      created: "پۆل دروستکرا",
      renamed: "ناوی پۆل گۆڕدرا",
      deleted: "پۆل سڕایەوە",
      confirmDelete: "ئەم پۆلە بسڕێتەوە؟ (تەنها ئەگەر هیچ قوتابیەکی تێدا نەبێت)",
      newTitle: "پۆلی نوێ",
      editTitle: "گۆڕینی ناوی پۆل",
      empty: "هێشتا هیچ پۆلێک نییە. یەکەم پۆلت زیاد بکە.",
    },
    importStudents: {
      button: "بارکردنی فایلی قوتابیان",
      title: "بارکردنی قوتابیان لە فایلەوە",
      help: "فایلی Excel (.xlsx) یان CSV بە ستوونەکانی: ناو، مۆبایل، ڕەگەز. ڕیزی یەکەم دەتوانێت سەردێڕ بێت.",
      group: "زیادکردنی قوتابیان بۆ پۆل",
      choose: "فایل هەڵبژێرە",
      upload: "بارکردن",
      uploading: "بارکردن...",
      done: "{created} قوتابی زیادکرا، {skipped} پشتگوێ خرا",
      template: "داگرتنی فایلی نموونە",
      cancel: "هەڵوەشاندنەوە",
    },
    backup: {
      title: "گواستنەوەی داتابەیس بۆ سێرڤەرێکی نوێ",
      help: "باکئەپێکی تەواوی هەموو داتاکانی قوتابخانە دابگرە (ڕێکخستن، پۆل، بەڕێوەبەر، قوتابی، ئامادەبوون، تۆمار). لە سێرڤەرە نوێیەکە وەک سوپەر ئەدمین بچۆ ژوورەوە و ئەو فایلە لێرە بگەڕێنەوە.",
      download: "داگرتنی باکئەپی تەواو",
      restore: "گەڕاندنەوە لە فایلی باکئەپ",
      confirm: "ئەم باکئەپە بگەڕێنرێتەوە؟ تۆمارە هاوشێوەکان بە وەشانی باکئەپ دەگۆڕدرێن.",
      restored: "باکئەپ بە سەرکەوتوویی گەڕێندرایەوە",
      working: "چاوەڕێ بە...",
      downloadFailed: "دابەزاندن سەرکەوتوو نەبوو",
    },
    settings: {
      title: "قوتابخانە و ڕووکار",
      subtitle: "ناسنامەی قوتابخانەکەت ڕێکبخە",
      schoolName: "ناوی قوتابخانە",
      logo: "لۆگۆی قوتابخانە",
      uploadLogo: "بارکردنی لۆگۆ",
      uploading: "بارکردن...",
      primaryColor: "ڕەنگی سەرەکی",
      backgroundColor: "ڕەنگی پاشبنەما",
      pageStyle: "شێوازی پەڕە",
      styleModern: "مۆدێرن",
      styleClassic: "کلاسیک",
      styleCompact: "بچووک",
      save: "پاشەکەوتی گۆڕانکاری",
      saved: "ڕێکخستنەکان پاشەکەوتکران",
      preview: "پێشبینین",
      welcomeSection: "شاشەی بەخێرهاتن / چوونەژوورەوە",
      welcomeImage: "وێنەی بەخێرهاتن",
      uploadWelcome: "بارکردنی وێنە",
      welcomeTitle: "سەردێڕی بەخێرهاتن",
      welcomeTitlePh: "بەتاڵی بهێڵەوە بۆ بەکارهێنانی بنەڕەت",
      welcomeSubtitle: "ژێردێڕی بەخێرهاتن",
      imagePosition: "شوێنی وێنە",
      posLeft: "چەپ",
      posRight: "ڕاست",
      posNone: "شاراوە (تەنها فۆرم)",
      textPosition: "شوێنی نووسین",
      posTop: "سەرەوە",
      posCenter: "ناوەڕاست",
      posBottom: "خوارەوە",
      textAlign: "ڕێکخستنی نووسین",
      alignStart: "سەرەتا",
      alignCenter: "ناوەڕاست",
      alignEnd: "کۆتایی",
    },
    common: { loading: "چاوەڕوانبە...", language: "زمان", cancel: "پاشگەزبوونەوە", confirm: "دڵنیابوونەوە" },
    monthly: {
      title: "ئامادەبوونی مانگانە",
      subtitle: "شوێنکەوتنی ئامادەبوون بۆ تەواوی مانگ",
      month: "مانگ",
      group: "پۆل / کۆمەڵ",
      allGroups: "هەموو پۆلەکان",
      student: "قوتابی",
      present: "ئامادە",
      absent: "ئامادەنەبوو",
      percentage: "٪",
      leave: "مۆڵەت",
      legend: "کرتە لە ڕۆژێک بکە: ئامادە ← ئامادەنەبوو ← مۆڵەت ← سڕینەوە",
      noData: "هیچ قوتابیەک نییە.",
      saved: "ئامادەبوون نوێکرایەوە",
      print: "چاپکردنی مانگ",
      reportTitle: "تۆماری ئامادەبوونی مانگانە",
    },
    absentees: {
      title: "تێڕوانینی ئامادەنەبووان",
      subtitle: "بینینی هەموو قوتابیانی ئامادەنەبوو یان لە مۆڵەت",
      byDate: "بەپێی بەروار",
      ranking: "کۆی گشتی / ڕیزبەندی",
      date: "بەروار",
      group: "پۆل / کۆمەڵ",
      allGroups: "هەموو پۆلەکان",
      from: "لە",
      to: "بۆ",
      absent: "ئامادەنەبوو",
      leave: "لە مۆڵەت",
      student: "قوتابی",
      number: "ژمارە",
      phone: "مۆبایل",
      totalAbsent: "کۆی ئامادەنەبوون",
      totalLeave: "کۆی مۆڵەت",
      percentage: "ڕێژەی ئامادەبوون",
      noAbsent: "هیچ ئامادەنەبوونێک لەم بەروارەدا نییە.",
      noLeave: "کەس لە مۆڵەت نییە لەم بەروارەدا.",
      noData: "هیچ ئامادەنەبوونێک لەم ماوەیەدا نییە.",
      refresh: "نوێکردنەوە",
      autoRefresh: "نوێکردنەوەی خۆکار",
      updated: "نوێکرایەوە",
      print: "چاپکردن",
    },
  },
};
